/**
 * SPARC-009: Global region toggle bar for Government Data dashboard header.
 * Visible on Opportunities, Weekly Review, Industry News, and Data Integrations tabs.
 *
 * SPARC-ISSUE-004 (Global Opportunities cluster): redesigned as interactive,
 * pill-shaped buttons with a consistent flag + full country name, clear
 * hover/focus/selected states, and a locked affordance for regions the
 * account can't access (instead of a plain disabled outline button).
 */

import React from 'react';
import { Lock } from 'lucide-react';
import { formatNumber } from '@tracekit/core';
import { useGeographicZone } from '../../contexts/GeographicZoneContext';
import {
  shouldShowWorldZone,
  WORLD_ZONE,
} from '../../utils/countryAccess';

export interface GeographicZoneToggleBarProps {
  /** Compact layout for PageHeader actions (no "Region:" label). */
  compact?: boolean;
  className?: string;
}

interface ZonePillProps {
  id: string;
  icon: string;
  label: string;
  count?: number;
  selected: boolean;
  allowed: boolean;
  title: string;
  onSelect: () => void;
}

function ZonePill({ icon, label, count, selected, allowed, title, onSelect }: ZonePillProps) {
  const countLabel = count != null ? formatNumber(count) : null;

  return (
    <button
      type="button"
      disabled={!allowed}
      title={title}
      aria-pressed={selected}
      onClick={onSelect}
      className={`group inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-1 ${
        selected
          ? 'border-primary-600 bg-primary-600 text-white shadow-sm shadow-primary-600/30'
          : allowed
            ? 'border-theme-border bg-theme-paper text-theme-text hover:border-primary-400 hover:bg-primary-50 hover:text-primary-700 hover:shadow-sm active:scale-95 dark:hover:bg-primary-900/20 dark:hover:text-primary-300'
            : 'cursor-not-allowed border-theme-border/60 bg-theme-surface-hover/40 text-theme-text-muted opacity-60'
      }`}
    >
      <span aria-hidden className="text-base leading-none">
        {icon}
      </span>
      <span>{label}</span>
      {countLabel != null && (
        <span
          className={`inline-flex min-w-[1.25rem] items-center justify-center rounded-full px-1.5 py-0.5 text-[11px] font-semibold leading-none ${
            selected
              ? 'bg-white/20 text-white'
              : count === 0
                ? 'bg-theme-surface-hover text-theme-text-muted'
                : 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
          }`}
        >
          {countLabel}
        </span>
      )}
      {!allowed && <Lock className="h-3 w-3 shrink-0 opacity-70" aria-hidden />}
    </button>
  );
}

export function GeographicZoneToggleBar({
  compact = false,
  className = '',
}: GeographicZoneToggleBarProps) {
  const {
    selectedGeographicZone,
    setSelectedGeographicZone,
    accessibleZones,
    zoneOpportunityCounts,
    zoneCountsLoaded,
    userCountryAccess,
  } = useGeographicZone();

  const visibleCountryZones = accessibleZones;
  const worldCount = zoneCountsLoaded ? zoneOpportunityCounts?.world : undefined;

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`}>
      {!compact && (
        <span className="text-sm font-medium text-theme-text mr-1">Region:</span>
      )}
      {shouldShowWorldZone(userCountryAccess) && (
        <ZonePill
          id={WORLD_ZONE.id}
          icon={WORLD_ZONE.icon}
          label={WORLD_ZONE.label}
          count={worldCount}
          selected={selectedGeographicZone === WORLD_ZONE.id}
          allowed
          title="Show all data your account can access (no region filter)"
          onSelect={() => setSelectedGeographicZone(WORLD_ZONE.id)}
        />
      )}
      {visibleCountryZones.map((zone) => {
        const isSelected = selectedGeographicZone === zone.id;
        const zoneCount = zoneCountsLoaded
          ? zoneOpportunityCounts?.[zone.id] ?? 0
          : undefined;
        return (
          <ZonePill
            key={zone.id}
            id={zone.id}
            icon={zone.icon}
            label={zone.label}
            count={zoneCount}
            selected={isSelected}
            allowed
            title={
              zoneCount != null
                ? `Show ${zone.label} data (${formatNumber(zoneCount)} open)`
                : `Show ${zone.label} data`
            }
            onSelect={() => setSelectedGeographicZone(zone.id)}
          />
        );
      })}
      {!compact && visibleCountryZones.length === 1 && (
        <span className="text-xs text-theme-text-secondary ml-1">
          Showing {visibleCountryZones[0].label} only (role-based access)
        </span>
      )}
    </div>
  );
}
